import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Table from "@/components/ui/shared/table/Table";
import TableHeader from "@/components/ui/shared/table/TableHeader";
import TableRow from "@/components/ui/shared/table/TableRow";
import ScrollArea from "@/components/ui/shared/ScrollArea";
import TableBody from "@/components/ui/shared/table/TableBody";
import TableCell from "@/components/ui/shared/table/TableCell";
import TableEdit from "@/components/settings/TableEdit";
import TagEdit from "@/components/settings/TagEdit";
import RespostasEdit from "@/components/settings/respostasEdit";
import EquipeEdit from "@/components/settings/EquipeEdit";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EtapaOptions, PipelineOptions, TagOptions } from "@/data/SelectOptions";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
// import bcrypt from "bcryptjs"; // Não mais necessário - usando Supabase Auth
import ConnectionTab from "@/components/settings/ConnectionTab";
import IntegrationsTab from "@/components/settings/IntegrationsTab";
import { PipelinesTab } from "@/components/settings/PipelinesTab";
import CustomFieldsTab from "@/components/settings/CustomFieldsTab";
import TagsTab from "@/components/settings/TagsTab";
import QuickRepliesTab from "@/components/settings/QuickRepliesTab";
import TeamTab from "@/components/settings/TeamTab";
import DistributionTab from "@/components/settings/DistributionTab";
import LogsTab from "@/components/settings/LogsTab";

interface ConnectionStatus {
  hasInstance: boolean;
  isConnected: boolean;
  qrCode?: string;
  instanceName?: string;
  loading: boolean;
  error?: string;
}

interface MenuItem {
  id: string;
  label: string;
  content: React.ReactNode;
}

const ProfileForm: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });
  
  // Password change states
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
  };

  const validatePasswordForm = () => {
    if (!passwordData.currentPassword) {
      setMessage({ type: 'error', text: 'Senha atual é obrigatória' });
      return false;
    }
    if (!passwordData.newPassword) {
      setMessage({ type: 'error', text: 'Nova senha é obrigatória' });
      return false;
    }
    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Nova senha deve ter pelo menos 6 caracteres' });
      return false;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Nova senha e confirmação não coincidem' });
      return false;
    }
    return true;
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setMessage(null);

    try {
      if (!supabase) {
        throw new Error('Supabase não está configurado')
      }

      const { error } = await supabase
        .from('account_users')
        .update({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
        })
        .eq('id', user.id);

      if (error) throw error;

      setMessage({ type: 'success', text: 'Perfil atualizado com sucesso!' });
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      setMessage({ type: 'error', text: 'Erro ao atualizar perfil' });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !validatePasswordForm()) return;

    setLoading(true);
    setMessage(null);

    try {
      // Nota: Validação de senha atual agora é feita pelo Supabase Auth
      // O campo 'password' foi removido de account_users após migração para auth.users

      if (!supabase) {
        throw new Error('Supabase não está configurado')
      }

      // Atualizar senha usando Supabase Auth
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (error) throw error;

      setMessage({ type: 'success', text: 'Senha alterada com sucesso!' });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      setMessage({ type: 'error', text: 'Erro ao alterar senha' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="xl:w-[70%] flex flex-col gap-6">
      {message && (
        <div className={`p-3 rounded-md ${
          message.type === 'success' 
            ? 'bg-green-500/20 border border-green-500/30 text-green-300' 
            : 'bg-red-500/20 border border-red-500/30 text-red-300'
        }`}>
          {message.text}
        </div>
      )}

      {/* Dados do Perfil */}
      <form onSubmit={handleUpdateProfile} className="flex flex-col gap-4">
        <h3 className="text-lg font-semibold text-white border-b border-[#3F3F464D] pb-2">
          Dados do Perfil
        </h3>
        
        <div className="form-group flex flex-col gap-3">
          <label htmlFor="name" className="text-white font-light">
            Nome
          </label>
          <Input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Nome"
            className="rounded-[6px] py-3 placeholder:text-neutral-400 text-white border border-neutral-500 bg-neutral-600 px-3"
            required
          />
        </div>

        <div className="form-group flex flex-col gap-3">
          <label htmlFor="email" className="text-white font-light">
            E-mail
          </label>
          <Input
            type="email"
            id="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="E-mail"
            className="rounded-[6px] py-3 placeholder:text-neutral-400 text-white border border-neutral-500 bg-neutral-600 px-3"
            required
          />
        </div>

        <div className="form-group flex flex-col gap-3">
          <label htmlFor="phone" className="text-white font-light">
            Telefone
          </label>
          <Input
            type="tel"
            id="phone"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            placeholder="Telefone"
            className="rounded-[6px] py-3 placeholder:text-neutral-400 text-white border border-neutral-500 bg-neutral-600 px-3"
            required
          />
        </div>

        <Button 
          type="submit" 
          className="bg-[#E4E4E7] min-w-[120px] mt-4 self-end"
          disabled={loading}
        >
          {loading ? 'Salvando...' : 'Salvar Perfil'}
        </Button>
      </form>

      {/* Alteração de Senha */}
      <div className="flex flex-col gap-4 border-t border-[#3F3F464D] pt-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-white">
            Alterar Senha
          </h3>
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowPasswordForm(!showPasswordForm)}
            className="min-w-[120px] border-white text-white hover:bg-white hover:text-black"
          >
            {showPasswordForm ? 'Cancelar' : 'Alterar Senha'}
          </Button>
        </div>

        {showPasswordForm && (
          <form onSubmit={handleChangePassword} className="flex flex-col gap-4">
            <div className="form-group flex flex-col gap-3">
              <label htmlFor="currentPassword" className="text-white font-light">
                Senha Atual
              </label>
              <Input
                type="password"
                id="currentPassword"
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                placeholder="Digite sua senha atual"
                className="rounded-[6px] py-3 placeholder:text-neutral-400 text-white border border-neutral-500 bg-neutral-600 px-3"
                required
              />
            </div>

            <div className="form-group flex flex-col gap-3">
              <label htmlFor="newPassword" className="text-white font-light">
                Nova Senha
              </label>
              <Input
                type="password"
                id="newPassword"
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                placeholder="Digite a nova senha (mín. 6 caracteres)"
                className="rounded-[6px] py-3 placeholder:text-neutral-400 text-white border border-neutral-500 bg-neutral-600 px-3"
                required
                minLength={6}
              />
            </div>

            <div className="form-group flex flex-col gap-3">
              <label htmlFor="confirmPassword" className="text-white font-light">
                Confirmar Nova Senha
              </label>
              <Input
                type="password"
                id="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                placeholder="Confirme a nova senha"
                className="rounded-[6px] py-3 placeholder:text-neutral-400 text-white border border-neutral-500 bg-neutral-600 px-3"
                required
              />
            </div>

            <Button 
              type="submit" 
              className="bg-[#E4E4E7] min-w-[120px] mt-4 self-end"
              disabled={loading}
            >
              {loading ? 'Alterando...' : 'Alterar Senha'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

const menuItems: MenuItem[] = [
  {
    id: "conexao",
    label: "Conexão",
    content: <ConnectionTab />,
  },
  {
    id: "perfil",
    label: "Perfil",
    content: <ProfileForm />,
  },
  {
    id: "campos",
    label: "Campos personalizados",
    content: <CustomFieldsTab />,
  },
  {
    id: "tags",
    label: "Tags",
    content: <TagsTab />,
  },
  {
    id: "respostas",
    label: "Respostas Rápidas",
    content: <QuickRepliesTab />,
  },
  {
    id: "equipe",
    label: "Equipe",
    content: <TeamTab />,
  },
  {
    id: "logs",
    label: "Logs",
    content: <LogsTab />,
  },

  {
    id: "distribuicao",
    label: "Distribuição Padrão",
    content: <DistributionTab />,
  },
  {
    id: "integracoes",
    label: "Integrações",
    content: (
      <IntegrationsTab />
    ),
  },
  {
    id: "pipelines",
    label: "Pipelines",
    content: (
      <PipelinesTab />
    ),
  },
];
const tabsWithAdd = ["logs"];
const Settings: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [selectedItem, setSelectedItem] = useState<MenuItem>(menuItems[0]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      const foundItem = menuItems.find(item => item.id === tab);
      if (foundItem) {
        setSelectedItem(foundItem);
      }
    }
  }, [searchParams]);

  const [checkedItems, setCheckedItems] = useState({
    item1: false,
    item2: false,
    item3: false,
    item4: false,
    item5: false,
    item6: false,
    item7: false,
    item8: false,
  });

  const itemNames = ["Dashboard", "Contatos", "Agenda", "CRM", "Chat", "Campanhas", "Assistentes", "Configurações"];

  const handleCheckboxChange = (itemId: string, checked: boolean) => {
    setCheckedItems((prev) => ({
      ...prev,
      [itemId]: checked,
    }));
  };

  const AddNewDialog = () => {
    switch (selectedItem.id) {
      case "campos":
        return (
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-[#E4E4E7] min-w-[120px] mb-8">Adicionar novo</Button>
            </DialogTrigger>
            <DialogContent className="flex w-full p-0 border-[#3F3F464D] bg-[#18181B] flex-col">
              <ScrollArea classNames={{ root: "max-h-[90dvh] p-5 overflow-auto" }}>
                <form action="" className="w-full mt-4 gap-4 flex flex-col">
                  {/* name  */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="name">Nome do campo</Label>
                    <Input
                      placeholder="Name"
                      id="name"
                      className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-[#71717A] min-h-[36px] border focus:ring-white border-[#3F3F46]"
                      type="text"
                    />
                  </div>
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="name">Tipo</Label>
                    <Select>
                      <SelectTrigger className="bg-neutral-600 text-[14px] w-full placeholder:text-[#71717A] text-[#71717A]  !h-[40px] border focus:ring-white border-[#3F3F46]">
                        <SelectValue placeholder="Tipo" />
                      </SelectTrigger>
                      <SelectContent className="">
                        <SelectGroup>
                          {TagOptions.map((item, index) => {
                            return (
                              <SelectItem key={index} value={item}>
                                {item}
                              </SelectItem>
                            );
                          })}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* extra field   */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="extra-field">Descrição</Label>
                    <Textarea
                      rows={4}
                      cols={4}
                      placeholder="Descrição"
                      id="extra-field"
                      className="bg-neutral-600 min-h-[150px]  text-[14px] placeholder:text-[#71717A] text-[#71717A]   border focus:ring-white border-[#3F3F46]"
                    />
                  </div>

                  <div className="w-full flex justify-end">
                    <Button onClick={(e) => e.preventDefault()} className="w-[120px]">
                      Adicionar novo
                    </Button>
                  </div>
                </form>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        );
      case "tags":
        return (
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-[#E4E4E7] min-w-[120px] mb-8">Adicionar novo</Button>
            </DialogTrigger>
            <DialogContent className="flex w-full p-0 border-[#3F3F464D] bg-[#18181B] flex-col">
              <ScrollArea classNames={{ root: "max-h-[90dvh] p-5 overflow-auto" }}>
                <form action="" className="w-full mt-4 gap-4 flex flex-col">
                  {/* name  */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="tag">Nome do tag</Label>
                    <Input
                      placeholder="Lorem"
                      id="tag"
                      className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-[#71717A] min-h-[36px] border focus:ring-white border-[#3F3F46]"
                      type="text"
                    />
                  </div>

                  <div className="w-full flex justify-end">
                    <Button onClick={(e) => e.preventDefault()} className="w-[120px]">
                      Adicionar novo
                    </Button>
                  </div>
                </form>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        );
      case "respostas":
        return (
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-[#E4E4E7] min-w-[120px] mb-8">Adicionar novo</Button>
            </DialogTrigger>
            <DialogContent className="flex w-full p-0 border-[#3F3F464D] bg-[#18181B] flex-col">
              <ScrollArea classNames={{ root: "max-h-[90dvh] p-5 overflow-auto" }}>
                <form action="" className="w-full mt-4 gap-4 flex flex-col">
                  {/* name  */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="name">Nome do resposta</Label>
                    <Input
                      placeholder="Name"
                      id="name"
                      className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-[#71717A] min-h-[36px] border focus:ring-white border-[#3F3F46]"
                      type="text"
                    />
                  </div>

                  {/* extra field   */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="extra-field">Descrição</Label>
                    <Textarea
                      rows={4}
                      cols={4}
                      placeholder="Descrição"
                      id="extra-field"
                      className="bg-neutral-600 min-h-[150px]  text-[14px] placeholder:text-[#71717A] text-[#71717A]   border focus:ring-white border-[#3F3F46]"
                    />
                  </div>
                  <div className="w-full flex justify-end">
                    <Button onClick={(e) => e.preventDefault()} className="w-[120px]">
                      Adicionar novo
                    </Button>
                  </div>
                </form>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        );
      case "equipe":
        return (
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-[#E4E4E7] min-w-[120px] mb-8">Adicionar novo</Button>
            </DialogTrigger>
            <DialogContent className="flex w-full p-0 border-[#3F3F464D] bg-[#18181B] flex-col">
              <ScrollArea classNames={{ root: "max-h-[90dvh] p-5 overflow-auto" }}>
                <form action="" className="w-full mt-4 gap-4 flex flex-col">
                  {/* name  */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="name">Nome do campo</Label>
                    <Input
                      placeholder="Name"
                      id="name"
                      className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-[#71717A] min-h-[36px] border focus:ring-white border-[#3F3F46]"
                      type="text"
                    />
                  </div>
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="name">E-mail</Label>
                    <Input
                      placeholder="E-mail"
                      id="email"
                      className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-[#71717A] min-h-[36px] border focus:ring-white border-[#3F3F46]"
                      type="email"
                    />
                  </div>
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="name">Telefone</Label>
                    <Input
                      placeholder="Telefone"
                      id="number"
                      className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-[#71717A] min-h-[36px] border focus:ring-white border-[#3F3F46]"
                      type="number"
                    />
                  </div>
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="name">Senha</Label>
                    <Input
                      placeholder="Senha "
                      id="senha"
                      className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-[#71717A] min-h-[36px] border focus:ring-white border-[#3F3F46]"
                      type="text"
                    />
                  </div>
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="name">Todos os contatos</Label>
                    <Select>
                      <SelectTrigger className="bg-neutral-600 !h-[38px] text-[14px] w-full placeholder:text-[#71717A] text-[#71717A]   border focus:ring-white border-[#3F3F46]">
                        <SelectValue placeholder="Todos os contatos" />
                      </SelectTrigger>
                      <SelectContent className=" ">
                        <SelectGroup>
                          {TagOptions.map((item, index) => {
                            return (
                              <SelectItem key={index} value={item}>
                                {item}
                              </SelectItem>
                            );
                          })}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className=" flex flex-col gap-4">
                    <p className=" underline text-[15px] font-light text-white">Selecionar todos</p>
                    <div className=" grid grid-cols-2 max-w-[250px] gap-3">
                      {Object.entries(checkedItems).map(([itemId, isChecked], index) => (
                        <div key={itemId} className="flex items-center    gap-3">
                          <Checkbox
                            id={itemId}
                            checked={isChecked}
                            onCheckedChange={(checked) => handleCheckboxChange(itemId, checked === true)}
                            className=" bg-white"
                          />
                          <label
                            htmlFor={itemId}
                            className="text-sm text-white font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            {itemNames[index]}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="w-full flex justify-end">
                    <Button onClick={(e) => e.preventDefault()} className="w-[120px]">
                      Adicionar novo
                    </Button>
                  </div>
                </form>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        );
      case "logs":
        return (
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-[#E4E4E7] min-w-[120px] mb-8">Adicionar novo</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg"> </DialogContent>
          </Dialog>
        );
      default:
        return null;
    }
  };

  return (
    <div className=" w-full    flex h-[calc(100dvh-110px)]  flex-col gap-10">
      <h2 className="text-[26px] mb-[10px] sm:text-[30px] font-semibold text-neutral-50">Configurações</h2>
      <div className=" flex p-6 pb-0 h-full  gap-4 border border-[#3F3F464D] rounded-[8px]">
        <div className="w-[187px]  ">
          <div className=" ">
            <nav className="space-y-1">
              {menuItems.map((item) => (
                <Button
                  key={item.id}
                  variant="ghost"
                  className={`w-full justify-start text-left h-auto p-3 ${
                    selectedItem.id === item.id
                      ? "bg-neutral-600 text-white"
                      : "text-gray-300 hover:bg-neutral-600 hover:text-white"
                  }`}
                  onClick={() => setSelectedItem(item)}
                >
                  {item.label}
                </Button>
              ))}
            </nav>
          </div>
        </div>

        <div className="flex-1    h-full    @container/settings ">
          <div className=" px-4 lg:px-12 flex flex-col">
            <div className=" flex flex-col  @sm/settings:flex-row items-center justify-between">
              <h1 className="text-2xl lg:text-3xl text-center font-medium text-white mb-8">{selectedItem.label}</h1>
              {tabsWithAdd.includes(selectedItem.id) && <AddNewDialog />}
            </div>
            <div className="h-full   overflow-x-scroll ">{selectedItem.content}</div>
            {tabsWithAdd.includes(selectedItem.id) && (
              <Button type="submit" className="bg-[#E4E4E7] min-w-[120px] mt-6  self-end  ">
                Salvar
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;